import { useAtom } from '@reatom/npm-react'
import styled, { css } from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Button } from '@blade/ui-kit'
import { Numeric } from '@blade/ui-kit'
import { nativeNetworkCurrencyAtom } from '@root/entities/currency'
import { useTranslation } from '@root/shared/localization'
import { mediaMinTablet } from '@root/shared/ui-kit'

import { ACTION_BUTTON_COLOR_MAP } from './consts'

import type {
  PurchaseBoostersReatom,
  PurchaseBoostersType,
  PurchaseBoostersValidationError,
} from '@root/features/premium/purchase-boosters'
import type { TransKey } from '@root/shared/localization'

type Props = {
  model: PurchaseBoostersReatom
  product: PurchaseBoostersType
}

const errorTypeToTransKey = {
  InsufficientBalance: 'premium.pay.error-ballance',
  NoWallet: 'premium.pay.no-wallet',
  NotEnoughBoostersForPremium: 'premium.pay.not-enough-boosters-for-premium',
  PortalLinkInvalid: 'premium.pay.portal-link.invalid',
  InvalidBoosters: 'common.invalid_input',
} satisfies Record<PurchaseBoostersValidationError['type'], TransKey>

export const PaymentFormAction: React.FC<Props> = ({ product, model }) => {
  const { t } = useTranslation()

  const [requestState] = useAtom(model.requestStateAtom)
  const [priceNative] = useAtom(model.boostersPriceNativeAtom)
  const [nativeCurrency] = useAtom(nativeNetworkCurrencyAtom)
  const [validationError] = useAtom(model.validationAtom)
  const [minimumBoosters] = useAtom(model.miniumBoostersAtom)

  if (validationError) {
    const errorMessage =
      validationError.type === 'NotEnoughBoostersForPremium'
        ? t('premium.pay.not-enough-boosters-for-premium', {
            boosters: minimumBoosters,
          })
        : t(errorTypeToTransKey[validationError.type])

    return (
      <ButtonAlt fullWidth size="m" disabled variant="soft" radius={'rounded'}>
        {errorMessage}
      </ButtonAlt>
    )
  }

  return (
    <ButtonAlt
      fullWidth
      color={ACTION_BUTTON_COLOR_MAP[product]}
      size="m"
      busy={AsyncStateN.isLoading(requestState)}
      type="submit"
      variant="glass"
      radius={'rounded'}
    >
      {t('premium.button.pay-amount')}
      <Numeric
        value={priceNative}
        preset="currency-amount-short"
        currency={nativeCurrency.ticker}
      />
    </ButtonAlt>
  )
}

const ButtonAlt = styled(Button)`
  text-transform: uppercase;

  ${mediaMinTablet(css`
    font-size: 12px;
    font-style: normal;
    font-weight: 700;
    text-transform: initial;
  `)}
`
